import { defineComponent as _defineComponent } from 'vue'
import type { CollectionType } from '@/enums/collection-type';
import type { TitleEventPayloadGraphql } from '@/helpers/title-helper';
import type { PopularTitleGraphqlFragment } from '@/pages/graphql/fragments/PopularTitle.fragment';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { UpcomingTitleGraphqlFragment } from '@/pages/graphql/fragments/UpcomingTitle.fragment';

import InfiniteBiScroll from '@/components/list/InfiniteBiScroll.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';

import { ImpressionTrackingEvents } from '@/enums/events';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { btoa } from '@/helpers/base64-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useMounted } from '@vueuse/core';
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { PaginationLevel } from '@/stores/pinia/useMetaStore';

type Title = { node: PopularTitleGraphqlFragment | UpcomingTitleGraphqlFragment };


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleListGrid',
  props: {
    hasNoTitles: { type: Boolean },
    isAtEndOfList: { type: Boolean },
    isFirstPage: { type: Boolean },
    loading: { type: Boolean },
    titles: null,
    titlesAmount: { default: 0 },
    titlesPerPage: { default: 100 },
    collectionType: null,
    sponsoredAd: { default: undefined },
    paginationLevel: { default: 0 }
  },
  emits: ["onPosterClick", "clickOut", "load-next", "load-prev"],
  setup(__props: any, { expose, emit: emits }: { emit: ({
	(e: 'onPosterClick', data: { title: TitleEventPayloadGraphql['title']; index: number; label: 'free' | '' }): void;
	(e: 'clickOut'): void;
	(e: 'load-next', data: any): void;
	(e: 'load-prev', data: any): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { hasNoTitles?: boolean, isAtEndOfList?: boolean, isFirstPage: boolean, loading: boolean, titles: Title[], titlesAmount: number, titlesPerPage: number, collectionType?: CollectionType, sponsoredAd: SponsoredAdFragment | undefined, paginationLevel: PaginationLevel };





const { filters } = useFilterStore();
const { isLoggedIn } = storeToRefs(useUserStore());
const { activeRoute } = storeToRefs(useRoutingStore());

const isMounted = useMounted();

const freeTagRoutes = [
	'app.titles.popular.list',
	'app.titles.popular.movie',
	'app.titles.popular.show',
	'app.titles.popular.subgenre',
	'app.titles.popular.subgenre-movie',
	'app.titles.popular.subgenre-show',
];
const showFreeTag = computed(() => freeTagRoutes.includes(activeRoute.value?.name as string) && !isLoggedIn.value);

const disableInfiniteScroll = computed(() => {
	const isTitleDetailPage = activeRoute.value?.name?.includes('app.detail');
	return props.hasNoTitles || props.isAtEndOfList || props.loading || isTitleDetailPage;
});

// CLS issue: upon filtering, the posters may change their position e.g. from 1st to 3rd.
// this change of order, together with a not-perfect mobile connection that will take more than 500ms
// for the filtering request, will be picked up as CLS: the existing element changes its position.
// to circumvent this, we give the posters a :key that is sort of bound to the request, so we can re-create
// the posters instead of being reused and moved through the DOM.
const requestHash = computed(() => {
	try {
		return btoa(JSON.stringify(filters(props.collectionType)));
	} catch {
		return props.collectionType?.toString();
	}
});

const shouldRender = computed(() => {
	return BUILD_CONFIG.globals.BUILD_TARGET === 'SPA' || isMounted.value;
});

/** Sponsored Recommendation */
const sponsoredRecommendationPackageId = computed(() => props.sponsoredAd?.campaign?.watchNowOffer.package.packageId);

const hasActiveSRCampaign = computed(
	() => props.sponsoredAd?.campaign != null && props.sponsoredAd?.holdoutGroup == false
);

// Check to see if we should pass in title context
function getTitleContext(title: Title, index: number) {
	return new SnowplowTitleContextGraphql(title.node.objectId, title.node.objectType, null, null, null, index);
}

function onPosterClick(title: Title, index: number): TitleEventPayloadGraphql {
	if (!!sponsoredRecommendationPackageId.value && props.sponsoredAd?.campaign?.node.nodeId === title.node.id) {
		TrackingHelper.trackEvent(
			ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
			{
				action: 'click',
				label: `${title.node.id}_${sponsoredRecommendationPackageId.value}`,
				property: 'poster',
			},
			[getTitleContext(title, index)]
		);
	}

	const label = showFreeTag.value && getFreeOffersCount(title) ? 'free' : '';

	emits('onPosterClick', { title: title.node as TitleEventPayloadGraphql['title'], index, label });

	return { title: title.node as TitleEventPayloadGraphql['title'], index, label };
}

function isLazyPoster(index: number) {
	return BUILD_CONFIG.globals.BUILD_TARGET === 'SSR' ? index >= global.IMG_POSTER_LOAD_COUNT : true;
}

function getFreeOffersCount({ node: title }: Title) {
	return (title as PopularTitleGraphqlFragment)?.freeOffersCount ?? 0;
}

function getTitle({ node: title }: Title) {
	return title.__typename === 'Season' ? title.show : title;
}

function getWatchNowOffer({ node: title }: Title) {
	return title.__typename === 'Season' ? title.show.watchNowOffer : title.watchNowOffer;
}

/**
 * Resets the internal state of the infinite scroll. Needs to be called at each filter change.
 * This method is used in parent component via reference
 */
// @ts-ignore the typing is correct for Vue 3 but Vue 2 sees typeof InfiniteScroll as '{}'
const infiniteScroll = ref<InstanceType<typeof InfiniteScroll> | null>(null);
function resetInfiniteScroll() {
	infiniteScroll.value?.reset();
}

expose({ resetInfiniteScroll });

return { __sfc: true,props, emits, filters, isLoggedIn, activeRoute, isMounted, freeTagRoutes, showFreeTag, disableInfiniteScroll, requestHash, shouldRender, sponsoredRecommendationPackageId, hasActiveSRCampaign, getTitleContext, onPosterClick, isLazyPoster, getFreeOffersCount, getTitle, getWatchNowOffer, infiniteScroll, resetInfiniteScroll, InfiniteBiScroll, InfiniteScroll, TitleDetailLink, PosterGraphql }
}

})